/**
 * Product Gallery Widget
 *
 * Standalone product image/video gallery extracted from ItemHeaderSection.
 * Handles: image upload, video upload, drag-reorder, primary selection,
 * delete mode, draft mode, and per-item replace (OPS-1554).
 *
 * Logic extracted to:
 *   useGalleryMutations.ts — mutations + handlers
 *   GalleryDialogs.tsx     — modals, banners, lightbox
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from '../types';
import { useLanguage } from '@/i18n/useLanguage';
import { getUITranslation } from '@/lib/views/labels/translations';
import { Image as ImageIcon, MoreVertical } from 'lucide-react';
import { SectionHeader } from '../../../SectionHeader';
import { useQuery } from '@tanstack/react-query';
import { getQueryOptions } from '@/lib/config/queryConfig';
import { getERPClient } from '@/lib/erp/client';
import { SortableGalleryGrid } from './SortableGalleryGrid';
import { type GalleryImage } from './SortableImageItem';
import { isVideoMedia } from './galleryMediaUtils';
import { AttachmentPickerDialog } from '@/components/attachments';

import { useAttachments } from '@/lib/attachments/hooks';

import { useGalleryMutations } from './useGalleryMutations';
import {
  GalleryActionsDialog,
  GalleryFloatingBanners,
  VideoLightbox,
  type GalleryLabels,
} from './GalleryDialogs';

// ── Labels ────────────────────────────────────────────────────────────────────

function useGalleryLabels(): GalleryLabels {
  const { language } = useLanguage();
  return useMemo(() => {
    const t = (key: string, fb: string) => getUITranslation(key, language) || fb;
    return {
      galleryActionsTitle: t('gallery_actions_title', 'Gallery Actions'),
      galleryActionsDescription: t('gallery_actions_description', 'Manage your product images gallery'),
      addImages: t('gallery_add_images', 'Add Images'),
      addImagesDescription: t('gallery_add_images_description', 'Upload new images to the gallery'),
      choosePrimaryImage: t('gallery_choose_primary_image', 'Choose primary image'),
      doneChoosingPrimary: t('gallery_done_choosing_primary', 'Done choosing primary'),
      choosePrimaryImageDescription: t('gallery_choose_primary_image_description', 'Select which image is the main product image'),
      exitPrimarySelectionDescription: t('gallery_exit_primary_selection_description', 'Exit primary selection mode'),
      rearrangeImages: t('gallery_rearrange_images', 'Rearrange Images'),
      doneRearranging: t('gallery_done_rearranging', 'Done rearranging'),
      rearrangeImagesDescription: t('gallery_rearrange_images_description', 'Drag images to reorder them'),
      exitRearrangeModeDescription: t('gallery_exit_rearrange_mode_description', 'Exit rearrange mode'),
      addVideos: t('gallery_add_videos', 'Add Videos'),
      maxVideosAllowed: t('gallery_max_videos_allowed', 'Maximum 3 videos allowed'),
      uploadVideosToGallery: t('gallery_upload_videos_to_gallery', 'Upload videos to the gallery'),
      choosePrimaryVideo: t('gallery_choose_primary_video', 'Choose primary video'),
      doneChoosingPrimaryVideo: t('gallery_done_choosing_primary_video', 'Done choosing primary video'),
      choosePrimaryVideoDescription: t('gallery_choose_primary_video_description', 'Select which video is the main product video'),
      exitPrimaryVideoSelectionDescription: t('gallery_exit_primary_video_selection_description', 'Exit primary video selection mode'),
      removeMedia: t('gallery_remove_media', 'Remove Media'),
      selectMediaToRemove: t('gallery_select_media_to_remove', 'Select images or videos to remove from the gallery'),
      noProductImagesTitle: t('gallery_no_product_images_title', 'No product images'),
      noProductImagesSubtitle: t('gallery_no_product_images_subtitle', 'Add images to showcase your product from multiple angles'),
      adding: t('gallery_adding', 'Adding...'),
      done: t('done', 'Done'),
      cancel: t('cancel', 'Cancel'),
      delete: t('delete', 'Delete'),
      removing: t('removing', 'Removing...'),
      bannerChoosePrimaryImage: t('gallery_banner_choose_primary_image', 'Choose primary image'),
      bannerChoosePrimaryImageHint: t('gallery_banner_choose_primary_image_hint', 'Tap the star icon on an image to set it as primary'),
      bannerChoosePrimaryVideo: t('gallery_banner_choose_primary_video', 'Choose primary video'),
      bannerChoosePrimaryVideoHint: t('gallery_banner_choose_primary_video_hint', 'Tap the video icon on a video to set it as primary'),
      bannerRearrangeTitle: t('gallery_banner_rearrange_title', 'Drag images to reorder them'),
      bannerRearrangeHint: t('gallery_banner_rearrange_hint', 'Use the drag handle to move images'),
      selectImagesToRemove: t('gallery_select_images_to_remove', 'Select images to remove'),
      tapImagesToSelect: t('gallery_tap_images_to_select', 'Tap images to select them'),
      imageSingular: t('gallery_image', 'image'),
      imagePlural: t('gallery_images', 'images'),
      selected: t('gallery_selected', 'selected'),
      confirmRemoveFromGalleryTemplate: t('gallery_confirm_remove_from_gallery', 'Remove {count} {imageWord} from the gallery?'),
    };
  }, [language]);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ProductGalleryWidget({
  section: _section,
  formData,
  onFieldChange: _onFieldChange,
  disabled,
  onSave: _onSave,
  isSaving: _isSaving,
  metaFields: _metaFields,
  isDraft = false,
  draftMedia,
  onDraftAddMedia,
  onDraftSetPrimary,
  onDraftReorder,
  onDraftRemoveMany,
}: ShopifySectionWidgetProps) {
  // ── UI State ──────────────────────────────────────────────────────────────
  const [isChoosingPrimary, setIsChoosingPrimary] = useState(false);
  const [isRearranging, setIsRearranging] = useState(false);
  const [isAddImagesDialogOpen, setIsAddImagesDialogOpen] = useState(false);
  const [isAddVideosDialogOpen, setIsAddVideosDialogOpen] = useState(false);
  const [isGalleryActionsDialogOpen, setIsGalleryActionsDialogOpen] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isChoosingPrimaryVideo, setIsChoosingPrimaryVideo] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [videoLightboxOpen, setVideoLightboxOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);

  // Replace flow state (OPS-1554)
  const [replaceTargetName, setReplaceTargetName] = useState<string | null>(null);
  const [isReplacePickerOpen, setIsReplacePickerOpen] = useState(false);
  const [replaceMediaType, setReplaceMediaType] = useState<'image' | 'video'>('image');

  const labels = useGalleryLabels();
  const itemCode = formData.item_code;
  const isDraftMode = isDraft || (!itemCode && !!draftMedia);

  // ── Gallery Data ──────────────────────────────────────────────────────────

  const attachmentContext = useMemo(() => ({ doctype: 'Item' as const, name: itemCode }), [itemCode]);
  const { data: docAttachments } = useAttachments(attachmentContext, {
    enabled: !!itemCode && !isDraftMode,
  });

  const { data: galleryData } = useQuery({
    queryKey: ['item-gallery', itemCode],
    queryFn: async () => {
      if (!itemCode) return null;
      return await getERPClient().call<{
        item_code: string;
        media: Array<{
          name: string; url: string; alt: string; type: 'image' | 'video';
          isPrimaryImage: boolean; isPrimaryVideo: boolean; idx: number; thumbnailUrl: string | null;
        }>;
        primary_image: string | null;
        primary_video: string | null;
        schema_version: string;
      }>('zaviago_backend.api.item_gallery.get_item_gallery', { item_code: itemCode, contract: 'v2' });
    },
    enabled: !!itemCode && !isDraftMode,
    ...getQueryOptions('detail'),
  });

  const draftGalleryImages: GalleryImage[] = useMemo(() => {
    if (!isDraftMode || !draftMedia) return [];
    return draftMedia.map((item, index) => ({
      name: `staged-${index}`,
      image: item.localPreviewUrl || item.file_url,
      alt_text: item.file_name || '',
      media_type: 'image' as const,
      is_primary: item.is_primary_image ? 1 : 0,
      is_primary_image: item.is_primary_image ? 1 : 0,
      is_primary_video: 0,
      idx: item.idx ?? index + 1,
    }));
  }, [isDraftMode, draftMedia]);

  const galleryImages: GalleryImage[] = useMemo(() => {
    if (isDraftMode) return draftGalleryImages;
    if (!galleryData?.media || !Array.isArray(galleryData.media)) return [];
    return galleryData.media.map((item) => ({
      name: item.name || '',
      image: item.url || '',
      alt_text: item.alt || '',
      media_type: item.type || 'image',
      is_primary: item.isPrimaryImage ? 1 : 0,
      is_primary_image: item.isPrimaryImage ? 1 : 0,
      is_primary_video: item.isPrimaryVideo ? 1 : 0,
      idx: item.idx || 0,
    } as GalleryImage));
  }, [isDraftMode, draftGalleryImages, galleryData?.media]);

  const videoCount = useMemo(
    () => galleryImages.filter((img) => isVideoMedia(img.media_type, img.image)).length,
    [galleryImages],
  );

  // ── Mutations ─────────────────────────────────────────────────────────────

  const {
    saveGalleryMutation,
    addImagesMutation,
    addVideosMutation,
    handleGalleryReorder,
    handleSetPrimary,
    handleRemoveSelectedImages,
    handleAddImagesCompleted,
    handleAddVideosCompleted,
    handleReplaceMedia,
  } = useGalleryMutations({
    itemCode,
    isDraftMode,
    galleryImages,
    videoCount,
    variantOf: (formData.variant_of as string) || undefined,
    draftMedia,
    onDraftReorder,
    onDraftSetPrimary,
    onDraftRemoveMany,
    onDraftAddMedia,
    docAttachments,
  });

  const isBusy = saveGalleryMutation.isPending || addImagesMutation.isPending || addVideosMutation.isPending;

  // ── Mode Handlers ─────────────────────────────────────────────────────────

  const exitAllModes = useCallback(() => {
    setIsChoosingPrimary(false);
    setIsChoosingPrimaryVideo(false);
    setIsRearranging(false);
    setIsDeleteMode(false);
    setSelectedImages(new Set());
  }, []);

  const handleToggleRearrange = useCallback(() => {
    if (!isRearranging) { exitAllModes(); }
    setIsRearranging((prev) => !prev);
  }, [isRearranging, exitAllModes]);

  const handleEnterDeleteMode = useCallback(() => {
    setIsGalleryActionsDialogOpen(false);
    exitAllModes();
    setIsDeleteMode(true);
  }, [exitAllModes]);

  const handleCancelDeleteMode = useCallback(() => {
    setIsDeleteMode(false);
    setSelectedImages(new Set());
  }, []);

  const handleToggleImageSelection = useCallback((imageName: string) => {
    setSelectedImages((prev) => {
      const next = new Set(prev);
      if (next.has(imageName)) next.delete(imageName);
      else next.add(imageName);
      return next;
    });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const imageWord = selectedImages.size > 1 ? labels.imagePlural : labels.imageSingular;
    const msg = labels.confirmRemoveFromGalleryTemplate
      .replace('{count}', String(selectedImages.size))
      .replace('{imageWord}', imageWord);
    if (confirm(msg)) {
      handleRemoveSelectedImages(selectedImages).then(() => {
        setSelectedImages(new Set());
        setIsDeleteMode(false);
      });
    }
  }, [selectedImages, labels, handleRemoveSelectedImages]);

  // ── Replace Flow (OPS-1554) ───────────────────────────────────────────────

  const handleStartReplace = useCallback((galleryItemName: string) => {
    const item = galleryImages.find((img) => img.name === galleryItemName);
    if (!item) return;
    setReplaceTargetName(galleryItemName);
    setReplaceMediaType(isVideoMedia(item.media_type, item.image) ? 'video' : 'image');
    setIsReplacePickerOpen(true);
  }, [galleryImages]);

  const handleReplaceCompleted = useCallback(async (result?: import('@/lib/attachments/types').GetAttachmentsResponse) => {
    if (!replaceTargetName || !result) {
      setIsReplacePickerOpen(false);
      setReplaceTargetName(null);
      return;
    }
    const success = await handleReplaceMedia(replaceTargetName, result);
    if (success) {
      setIsReplacePickerOpen(false);
      setReplaceTargetName(null);
    }
  }, [replaceTargetName, handleReplaceMedia]);

  // ── Render ────────────────────────────────────────────────────────────────

  const hasImages = galleryImages.length > 0;
  const isInActionMode = isChoosingPrimary || isChoosingPrimaryVideo || isRearranging || isDeleteMode;

  return (
    <>
      <div>
        <SectionHeader label="Product Images & Overview" />
        <Card
          className={cn(
            CARD,
            !isInActionMode && !disabled && (itemCode || isDraftMode) && !isBusy && 'cursor-pointer hover:shadow-md transition-shadow',
          )}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (!isInActionMode && !disabled && (itemCode || isDraftMode) && !isBusy && !target.closest('button') && !target.closest('a')) {
              setIsGalleryActionsDialogOpen(true);
            }
          }}
        >
          <CardContent
            className="p-8 sm:p-10"
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (isInActionMode || target.closest('button') || target.closest('a') || target.closest('img')) {
                e.stopPropagation();
              }
            }}
          >
            {/* Three-dot menu */}
            <div className="flex items-center justify-end mb-4">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsGalleryActionsDialogOpen(true); }}
                disabled={disabled || (!itemCode && !isDraftMode) || isBusy}
                className="group flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MoreVertical className="h-5 w-5 text-primary" />
              </button>
            </div>

            {/* Empty state */}
            {!hasImages && (
              <div
                className="flex flex-col items-center justify-center py-12 px-4 rounded border-2 border-dashed border-border/40 bg-muted/20"
                onClick={(e) => e.stopPropagation()}
              >
                <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-foreground mb-2">{labels.noProductImagesTitle}</p>
                <p className="text-xs text-muted-foreground mb-4 text-center max-w-sm">{labels.noProductImagesSubtitle}</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsAddImagesDialogOpen(true); }}
                  disabled={disabled || (!itemCode && !isDraftMode) || addImagesMutation.isPending}
                  className="px-4 py-2 rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addImagesMutation.isPending ? labels.adding : labels.addImages}
                </button>
              </div>
            )}

            {/* Gallery grid */}
            {hasImages && (
              <div
                className={cn(
                  'flex flex-col gap-4',
                  isInActionMode && 'pb-24',
                  !isInActionMode && 'cursor-pointer',
                )}
                data-gallery-grid
                onClick={(e) => { if (isInActionMode) { e.stopPropagation(); } }}
              >
                <SortableGalleryGrid
                  images={galleryImages}
                  onReorder={handleGalleryReorder}
                  onSetPrimary={handleSetPrimary}
                  isSaving={isBusy}
                  disabled={disabled || isDeleteMode}
                  isChoosingPrimary={isChoosingPrimary}
                  isChoosingPrimaryVideo={isChoosingPrimaryVideo}
                  isDeleteMode={isDeleteMode}
                  isRearranging={isRearranging}
                  selectedImages={selectedImages}
                  onToggleSelection={handleToggleImageSelection}
                  onVideoClick={(videoUrl) => {
                    setCurrentVideoUrl(videoUrl);
                    setVideoLightboxOpen(true);
                  }}
                  onReplaceItem={handleStartReplace}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attachment pickers */}
      {(itemCode || isDraftMode) && (
        <AttachmentPickerDialog
          open={isAddImagesDialogOpen}
          onOpenChange={setIsAddImagesDialogOpen}
          context={{ doctype: 'Item', name: isDraftMode ? 'new' : itemCode }}
          multiple
          accept={['image/*']}
          // Upload-only for BOTH draft and saved items. In 'attach' mode the
          // picker's onCompleted returns ALL document attachments (via
          // attachResult), so handleAddImagesCompleted re-injected every
          // orphaned File attachment as a duplicate gallery row on each upload.
          // Upload-only reports only the freshly-uploaded queue, so exactly the
          // images the user just added get appended. The gallery persists URLs
          // in the item_gallery child table, not via Item File attachments.
          mode="upload-only"
          onCompleted={handleAddImagesCompleted}
          stagedFileUrls={isDraftMode && draftMedia ? draftMedia.map((img) => img.file_url).filter(Boolean) : undefined}
        />
      )}

      {isAddVideosDialogOpen && (
        <AttachmentPickerDialog
          open={isAddVideosDialogOpen}
          onOpenChange={setIsAddVideosDialogOpen}
          context={{ doctype: 'Item', name: itemCode }}
          multiple
          accept={['video/*']}
          mode="attach"
          onCompleted={async (result) => {
            const ok = await handleAddVideosCompleted(result);
            if (ok) setIsAddVideosDialogOpen(false);
          }}
        />
      )}

      {/* Replace picker (OPS-1554) */}
      {isReplacePickerOpen && (
        <AttachmentPickerDialog
          open={isReplacePickerOpen}
          onOpenChange={(open) => {
            if (!open) { setIsReplacePickerOpen(false); setReplaceTargetName(null); }
          }}
          context={{ doctype: 'Item', name: itemCode }}
          multiple={false}
          accept={replaceMediaType === 'video' ? ['video/*'] : ['image/*']}
          mode="attach"
          onCompleted={handleReplaceCompleted}
        />
      )}

      {/* Gallery Actions Dialog */}
      <GalleryActionsDialog
        open={isGalleryActionsDialogOpen}
        onOpenChange={setIsGalleryActionsDialogOpen}
        labels={labels}
        hasImages={hasImages}
        videoCount={videoCount}
        disabled={disabled}
        itemCode={itemCode}
        isDraftMode={isDraftMode}
        isChoosingPrimary={isChoosingPrimary}
        isChoosingPrimaryVideo={isChoosingPrimaryVideo}
        isRearranging={isRearranging}
        isSaving={saveGalleryMutation.isPending}
        isAddingImages={addImagesMutation.isPending}
        isAddingVideos={addVideosMutation.isPending}
        onAddImages={() => { setIsGalleryActionsDialogOpen(false); setIsAddImagesDialogOpen(true); }}
        onToggleChoosePrimary={() => {
          setIsGalleryActionsDialogOpen(false);
          if (!isChoosingPrimary) { exitAllModes(); }
          setIsChoosingPrimary((prev) => !prev);
        }}
        onToggleRearrange={() => { setIsGalleryActionsDialogOpen(false); handleToggleRearrange(); }}
        onAddVideos={() => { setIsGalleryActionsDialogOpen(false); setIsAddVideosDialogOpen(true); }}
        onToggleChoosePrimaryVideo={() => {
          setIsGalleryActionsDialogOpen(false);
          if (!isChoosingPrimaryVideo) { exitAllModes(); }
          setIsChoosingPrimaryVideo((prev) => !prev);
        }}
        onEnterDeleteMode={handleEnterDeleteMode}
      />

      {/* Floating banners */}
      <GalleryFloatingBanners
        labels={labels}
        isDeleteMode={isDeleteMode}
        isRearranging={isRearranging}
        isChoosingPrimary={isChoosingPrimary}
        isChoosingPrimaryVideo={isChoosingPrimaryVideo}
        selectedCount={selectedImages.size}
        isSaving={saveGalleryMutation.isPending}
        onCancelDelete={handleCancelDeleteMode}
        onConfirmDelete={handleConfirmDelete}
        onToggleRearrange={handleToggleRearrange}
        onDoneChoosingPrimary={() => setIsChoosingPrimary(false)}
        onDoneChoosingPrimaryVideo={() => setIsChoosingPrimaryVideo(false)}
      />

      {/* Video lightbox */}
      <VideoLightbox
        open={videoLightboxOpen}
        onOpenChange={setVideoLightboxOpen}
        videoUrl={currentVideoUrl}
      />
    </>
  );
}
